import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  MenuItem,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DataTable from "../../components/brand/DataTable";
import { getBudget, approveSupplierInvoice, createSupplierInvoice, getSupplierInvoice, getSupplierInvoices } from "../../api/financeApi";
import { getPurchaseOrderComparison, getPurchaseOrderLookup, getPurchaseOrders } from "../../api/purchaseOrderApi";
import { getApiErrorMessage, isConcurrencyConflict } from "../../utils/concurrency";
import { formatDateUk } from "../../utils/dateDisplay";

const money = (value) => {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  return Number.isFinite(amount)
    ? new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount)
    : "—";
};

const toPence = (value) => {
  if (value == null || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
};

const fileHref = (url) => {
  if (!url) return "";
  if (/^https?:/i.test(url)) return url;
  const origin = process.env.REACT_APP_ADMIN_URL || "http://localhost:8000";
  return `${origin}${url.startsWith("/") ? url : `/${url}`}`;
};

const emptyForm = () => ({
  order: null,
  invoice_number: "",
  invoice_date: "",
  due_date: "",
  amount: "",
  document: null,
});

const yearForDate = (years, invoiceDate, fallback) => {
  if (invoiceDate) {
    const match = years.find((year) => year.start_date && year.end_date && year.start_date <= invoiceDate && invoiceDate <= year.end_date);
    if (match) return match;
  }
  return fallback || null;
};

async function loadYears(propertyId) {
  let page = 1;
  const rows = [];
  let fallback = null;
  while (page < 20) {
    const { data } = await getPurchaseOrderLookup(propertyId, "financial-years", { page, page_size: 100 });
    rows.push(...(data.results || []));
    fallback = data.default || fallback;
    if (rows.length >= (data.total || 0) || !(data.results || []).length) break;
    page += 1;
  }
  return { rows, fallback };
}

async function loadBillableOrders(propertyId, fiscalYearId) {
  let page = 1;
  const rows = [];
  while (page < 20) {
    const { data } = await getPurchaseOrders(propertyId, {
      status: "active", billable: "1", fiscal_year_id: fiscalYearId, page, page_size: 100,
    });
    rows.push(...(data.results || []));
    if (rows.length >= (data.total || 0) || !(data.results || []).length) break;
    page += 1;
  }
  return rows;
}

async function loadBudgetLines(propertyId, fiscalYearId) {
  const comparison = await getPurchaseOrderComparison(propertyId, { fiscal_year_id: fiscalYearId, page: 1, page_size: 1 });
  if (!comparison.data.budget?.id) return { budget: null, lines: [] };
  const { data } = await getBudget(propertyId, comparison.data.budget.id);
  const lines = (data.sections || []).flatMap((section) => (section.lines || []).map((line) => ({
    id: line.id,
    account_id: line.account_id,
    label: `${section.name} — ${line.name}`,
    account: line.account_code,
    amount: line.amount,
  })));
  return { budget: { id: data.id, name: data.name, status: data.status }, lines };
}

export default function SupplierBillsPanel({ propertyId, onMessage }) {
  const [bills, setBills] = useState({ results: [], total: 0 });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [years, setYears] = useState([]);
  const [defaultYear, setDefaultYear] = useState(null);
  const [orders, setOrders] = useState([]);
  const [budgetLines, setBudgetLines] = useState([]);
  const [contextError, setContextError] = useState("");
  const [contextLoading, setContextLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [approveTarget, setApproveTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    getSupplierInvoices(propertyId, page + 1, { page_size: size, ...(status ? { status } : {}) })
      .then(({ data }) => { if (!cancelled) setBills(data); })
      .catch((err) => { if (!cancelled) setError(getApiErrorMessage(err, "Failed to load supplier bills.")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [propertyId, page, size, status, refresh]);

  const formOpen = Boolean(form);
  const invoiceDate = form?.invoice_date || "";

  useEffect(() => {
    if (!formOpen) return undefined;
    let cancelled = false;
    loadYears(propertyId).then(({ rows, fallback }) => {
      if (!cancelled) { setYears(rows); setDefaultYear(fallback); }
    }).catch((err) => {
      if (!cancelled) setContextError(getApiErrorMessage(err, "Could not load financial years."));
    });
    return () => { cancelled = true; };
  }, [formOpen, propertyId]);

  const year = yearForDate(years, invoiceDate, defaultYear);

  useEffect(() => {
    if (!formOpen || !year?.id) return undefined;
    let cancelled = false;
    setContextLoading(true);
    setContextError("");
    Promise.all([loadBillableOrders(propertyId, year.id), loadBudgetLines(propertyId, year.id)])
      .then(([nextOrders, budget]) => {
        if (cancelled) return;
        setOrders(nextOrders);
        setBudgetLines(budget.lines);
        if (!budget.budget) setContextError("No agreed budget covers this invoice date.");
      })
      .catch((err) => { if (!cancelled) setContextError(getApiErrorMessage(err, "Could not load purchase orders.")); })
      .finally(() => { if (!cancelled) setContextLoading(false); });
    return () => { cancelled = true; };
  }, [formOpen, year?.id, propertyId]);

  const selectedOrder = orders.find((order) => String(order.id) === String(form?.order)) || null;
  const budgetLine = useMemo(
    () => budgetLines.filter((line) => Number(line.account_id) === Number(selectedOrder?.account_id)),
    [budgetLines, selectedOrder]
  );
  const amountPence = toPence(form?.amount);
  const remainingPence = toPence(selectedOrder?.remaining);
  const amountError = selectedOrder && amountPence != null && remainingPence != null && amountPence > remainingPence
    ? `This bill exceeds the ${money(selectedOrder.remaining)} still to bill on ${selectedOrder.po_number}.`
    : "";

  const edit = (field, value) => {
    setFormError("");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const chooseOrder = (orderId) => {
    const order = orders.find((item) => String(item.id) === String(orderId));
    setFormError("");
    setForm((current) => ({ ...current, order: orderId, amount: order ? String(order.remaining) : "" }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!selectedOrder) { setFormError("Select a purchase order."); return; }
    if (budgetLine.length === 0) { setFormError("The agreed budget has no line for this purchase order account."); return; }
    if (amountError || amountPence == null || amountPence <= 0) { setFormError(amountError || "Enter the amount of this bill."); return; }
    setSaving(true);
    setFormError("");
    const body = new FormData();
    body.append("purchase_order_id", selectedOrder.id);
    body.append("invoice_number", form.invoice_number.trim());
    body.append("invoice_date", form.invoice_date);
    if (form.due_date) body.append("due_date", form.due_date);
    body.append("gross_amount", form.amount);
    if (form.document) body.append("document", form.document);
    try {
      const { data } = await createSupplierInvoice(propertyId, body);
      setForm(null);
      setRefresh((value) => value + 1);
      onMessage?.(`Supplier bill ${data.invoice_number} saved against ${data.po_number}.`);
    } catch (err) {
      setFormError(getApiErrorMessage(err, "Failed to save supplier bill."));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (bill) => {
    setDetailError("");
    setDetail(bill);
    try {
      const { data } = await getSupplierInvoice(propertyId, bill.id);
      setDetail(data);
    } catch (err) {
      setDetailError(getApiErrorMessage(err, "Failed to load supplier bill."));
    }
  };

  const approve = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await approveSupplierInvoice(propertyId, approveTarget.id, approveTarget.row_version);
      onMessage?.(`Supplier bill ${data.invoice_number} approved and posted to the ledger.`);
      setApproveTarget(null);
      setDetail(null);
      setRefresh((value) => value + 1);
    } catch (err) {
      setError(isConcurrencyConflict(err)
        ? "This bill changed elsewhere. Close it and open it again."
        : getApiErrorMessage(err, "Failed to approve supplier bill."));
      setApproveTarget(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Typography variant="h5">Supplier bills</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setFormError(""); setContextError(""); setForm(emptyForm()); }}>New supplier bill</Button>
      </Box>
      <Typography variant="body2" sx={{ mb: 2 }}>A supplier bill is raised from a purchase order. Approving it posts the amount to that order’s account and reduces what is left to bill.</Typography>
      <TextField select size="small" label="Status" value={status} sx={{ minWidth: 160, mb: 2 }} onChange={(event) => { setStatus(event.target.value); setPage(0); }}>
        <MenuItem value="">All statuses</MenuItem>
        <MenuItem value="draft">Draft</MenuItem>
        <MenuItem value="approved">Approved</MenuItem>
        <MenuItem value="void">Void</MenuItem>
      </TextField>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CircularProgress size={24} aria-label="Loading supplier bills" />}
      <Box sx={{ overflowX: "auto" }}>
        <DataTable aria-label="Supplier bills">
          <TableHead>
            <TableRow>
              {["Invoice", "Purchase order", "Supplier", "Invoice date", "Due date", "Gross", "Status", "PDF", "Actions"].map((label) => <TableCell key={label}>{label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {bills.results.map((bill) => (
              <TableRow key={bill.id} hover>
                <TableCell>{bill.invoice_number}</TableCell>
                <TableCell>{bill.po_number || "—"}</TableCell>
                <TableCell>{bill.supplier_name}</TableCell>
                <TableCell>{formatDateUk(bill.invoice_date)}</TableCell>
                <TableCell>{bill.due_date ? formatDateUk(bill.due_date) : "—"}</TableCell>
                <TableCell>{money(bill.gross_amount)}</TableCell>
                <TableCell sx={{ textTransform: "capitalize" }}>{bill.status}{bill.status === "approved" && bill.outstanding != null ? ` · ${money(bill.outstanding)} outstanding` : ""}</TableCell>
                <TableCell>{bill.document_url ? <Link href={fileHref(bill.document_url)} target="_blank" rel="noopener noreferrer">PDF</Link> : "—"}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" size="small" startIcon={<VisibilityIcon />} onClick={() => openDetail(bill)}>View</Button>
                    {bill.status === "draft" && <Button variant="outlined" size="small" startIcon={<TaskAltIcon />} onClick={() => setApproveTarget(bill)}>Approve</Button>}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!loading && bills.results.length === 0 && <TableRow><TableCell colSpan={9}>No supplier bills match these filters.</TableCell></TableRow>}
          </TableBody>
        </DataTable>
      </Box>
      <TablePagination component="div" count={bills.total || 0} page={page} rowsPerPage={size} rowsPerPageOptions={[10, 25, 50]} onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setSize(Number(event.target.value)); setPage(0); }} />

      <Dialog open={formOpen} onClose={() => { if (!saving) setForm(null); }} fullWidth maxWidth="sm">
        {form && <Box component="form" onSubmit={save}>
          <DialogTitle>New supplier bill</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
              {(formError || amountError || contextError) && <Alert severity="error">{formError || amountError || contextError}</Alert>}
              <TextField required select label="Purchase order" value={form.order || ""} disabled={saving || contextLoading} onChange={(event) => chooseOrder(event.target.value)}>
                <MenuItem value="">Choose a purchase order</MenuItem>
                {orders.map((order) => <MenuItem key={order.id} value={String(order.id)}>{order.po_number} — {order.supplier_name} — {order.account_code} {order.account_name} — {money(order.remaining)} left</MenuItem>)}
              </TextField>
              {!contextLoading && orders.length === 0 && <Alert severity="info">There is no purchase order with an amount left to bill.</Alert>}
              {selectedOrder && <Typography variant="body2">{selectedOrder.supplier_name}. Account {selectedOrder.account_code} — {selectedOrder.account_name}. {money(selectedOrder.billed)} billed of {money(selectedOrder.amount)}, {money(selectedOrder.remaining)} left.</Typography>}
              {selectedOrder && budgetLine.length > 0 && <Typography variant="body2">Budget {budgetLine.length === 1 ? "line" : "lines"} {budgetLine.map((line) => `${line.label} (${money(line.amount)})`).join(", ")}.</Typography>}
              {selectedOrder && !contextLoading && budgetLine.length === 0 && <Alert severity="warning">The agreed budget has no line on {selectedOrder.account_code} {selectedOrder.account_name}.</Alert>}
              <TextField required label="Invoice number" value={form.invoice_number} onChange={(event) => edit("invoice_number", event.target.value)} disabled={saving} />
              <TextField required label="Invoice date" type="date" InputLabelProps={{ shrink: true }} value={form.invoice_date} onChange={(event) => edit("invoice_date", event.target.value)} disabled={saving} />
              <TextField label="Due date" type="date" InputLabelProps={{ shrink: true }} value={form.due_date} onChange={(event) => edit("due_date", event.target.value)} disabled={saving} />
              <TextField required label="Amount GBP" type="number" inputProps={{ min: "0.01", step: "0.01", max: selectedOrder?.remaining }} value={form.amount} onChange={(event) => edit("amount", event.target.value)} disabled={saving || !selectedOrder} />
              <Button variant="outlined" component="label" startIcon={<AttachFileIcon />} disabled={saving}>
                {form.document ? form.document.name : "Attach PDF"}
                <input hidden type="file" accept="application/pdf,.pdf" onChange={(event) => edit("document", event.target.files?.[0] || null)} />
              </Button>
              <Typography variant="body2">The PDF is optional. There is no VAT. The amount is posted to the purchase order account.</Typography>
              {contextLoading && <CircularProgress size={24} aria-label="Loading purchase orders" />}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" startIcon={<CloseIcon />} disabled={saving} onClick={() => setForm(null)}>Close</Button>
            <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} disabled={saving || contextLoading || !selectedOrder || budgetLine.length === 0}>{saving ? "Saving…" : "Save supplier bill"}</Button>
          </DialogActions>
        </Box>}
      </Dialog>

      <Dialog open={Boolean(detail)} onClose={() => { if (!saving) setDetail(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Supplier bill {detail?.invoice_number}</DialogTitle>
        <DialogContent>
          {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}
          {detail && <Box sx={{ display: "grid", gap: 1 }}>
            <Typography>{detail.supplier_name}{detail.po_number ? ` · ${detail.po_number}` : ""}</Typography>
            <Typography>Invoice date {formatDateUk(detail.invoice_date)}{detail.due_date ? ` · due ${formatDateUk(detail.due_date)}` : ""}</Typography>
            <Typography>Gross {money(detail.gross_amount)} · {detail.status}</Typography>
            {detail.document_url && <Link href={fileHref(detail.document_url)} target="_blank" rel="noopener noreferrer">Open PDF</Link>}
            {(detail.allocations || []).map((allocation) => <Typography key={allocation.id}>{allocation.budget_line_name}: {money(allocation.amount)}</Typography>)}
            {detail.allocations == null && <CircularProgress size={24} aria-label="Loading bill detail" />}
          </Box>}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" startIcon={<CloseIcon />} disabled={saving} onClick={() => setDetail(null)}>Close</Button>
          {detail?.status === "draft" && <Button variant="contained" startIcon={<TaskAltIcon />} disabled={saving} onClick={() => setApproveTarget(detail)}>Approve</Button>}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(approveTarget)} onClose={() => { if (!saving) setApproveTarget(null); }}>
        <DialogTitle>Approve {approveTarget?.invoice_number}?</DialogTitle>
        <DialogContent><DialogContentText>Approving posts this bill to the ledger and reduces what is left on the purchase order. It can no longer be edited.</DialogContentText></DialogContent>
        <DialogActions>
          <Button variant="outlined" startIcon={<CloseIcon />} disabled={saving} onClick={() => setApproveTarget(null)}>Keep as draft</Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <TaskAltIcon />} disabled={saving} onClick={approve}>Approve and post</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
